<template>
  <side-bar v-if="open" class="project-settings__container">
    <div class="language-settings">
      <menu-group class="itemheader">
        <h1 class="itemheader__title">
          {{ $t("labels.languages") }}
        </h1>
        <button
          type="button"
          class="button-icon itemheader__closebutton"
          @click="open = false"
        >
          <lucide-icon name="x" />
        </button>
      </menu-group>
      <menu-group>
        <div class="language-settings__languages">
          <radio-button
            v-for="(language, localeCode) in locales"
            :key="localeCode"
            :data="(localeCode as ValidLanguage)"
            :value="locale"
            @update:value="locale = $event"
          >
            <img
              class="language-settings__icon"
              :src="`${publicPath}img/flags/${localeCode}.png`"
            />
            {{ language }}
          </radio-button>
        </div>
      </menu-group>
    </div>
  </side-bar>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { setLocale } from "@/i18n";
import { useI18n } from "vue-i18n";
import envVars from "@/helpers/Env";

import LucideIcon from "@core/ui/LucideIcon.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import RadioButton from "@core/input/RadioButton.vue";
import SideBar from "@core/ui/SideBar.vue";

import { useSettingsPanel } from "@core/ui/settings.store";

import type { ValidLanguage } from "@/i18n/ValidLanguages";

const i18n = useI18n();

const publicPath = envVars.baseUrl ?? "/";

const open = computed({
  get() {
    return useSettingsPanel().languageSettingsOpen;
  },
  set(val: boolean) {
    useSettingsPanel().setLanguageSettingsOpen(val);
  }
});

const locales = computed(() => {
  const locales = i18n.availableLocales as ValidLanguage[];
  const result: { [locale: string]: string } = {};
  for (const locale of locales) {
    result[locale] = i18n.t(`locales.${locale}`);
  }
  return result;
});

const locale = computed({
  get() {
    return i18n.locale.value as ValidLanguage;
  },
  set(newLocale: ValidLanguage) {
    setLocale(newLocale);
  }
});
</script>

<style lang="scss">
.language-settings {
  text-align: left;
  position: relative;
  z-index: 2;

  &__container {
    position: absolute !important;
    right: 0;
    top: 0;
    height: 100%;
    z-index: 2;
  }

  &__languages {
    display: block;
    padding-top: 1rem;
  }

  &__icon {
    display: block;
    width: 2em;
    height: 2em;
    margin-right: 0.5em;
    margin-left: 0.5em;
  }
}
</style>
